import { useState, useEffect, useCallback, useMemo, useImperativeHandle, useRef } from 'react';
import externalAxios from 'axios';
import { useDropzone } from 'react-dropzone';
import { InformationCircleIcon, PhotoIcon } from '@heroicons/react/20/solid';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import {
    ArrowsPointingOutIcon,
    StarIcon as StarIconOutline,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import PhotoLightbox from './PhotoLightbox';
import axios from '../../utils/axios';
import { toastWarn, toastError } from '../../utils/toast';
import ConfidenceBadge from '../../components/AIEnrichment/ConfidenceBadge';
import WizardStepLayout from './WizardStepLayout';
import { WIZARD_PAGE_COPY } from './wizardCopy';
import WizardStepFooter from './WizardStepFooter';
import { SaveContinueLaterButton } from './wizardSaveLater';
import usePhotosStepBodyScrollLock from './usePhotosStepBodyScrollLock';
import { useWizard, isMissingAddressAutosaveError } from './WizardContext';
import useStepAutosave from './useStepAutosave';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const SOURCE_LABELS = {
    places:     'Google Places',
    streetview: 'Street View',
    satellite:  'Satellite',
};

const SOURCE_DISPLAY = {
    places:     'Google Places',
    streetview: 'Street View',
    satellite:  'satellite imagery',
};

const PHOTO_SECTION_TITLE =
    'text-[11px] font-bold uppercase tracking-[0.6px] text-gray-400 mb-2';

const COVERAGE_CATEGORIES = ['Entrance', 'Signage', 'Lot View', 'Exterior', 'Night Shot'];

/** Photo AI quality labels map to confidence levels (park-v4 `.confidence-badge`). */
const PHOTO_QUALITY_TO_CONFIDENCE = {
    High:   'high',
    Medium: 'medium',
    Low:    'low',
};

class UploadUrlsError extends Error {
    constructor() {
        super('Invalid upload URLs response');
        this.name = 'UploadUrlsError';
    }
}

function assertUploadUrls(uploadUrls, expectedCount) {
    if (!Array.isArray(uploadUrls) || uploadUrls.length !== expectedCount) {
        throw new UploadUrlsError();
    }
    const invalid = uploadUrls.some(
        url => typeof url !== 'string' || !/^https:\/\//i.test(url)
    );
    if (invalid) {
        throw new UploadUrlsError();
    }
}

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

const photoUrl = (photo) => photo?.url || photo?.preview || '';

/** Identity snapshot used by the dirty-check — shared by the mount/hydration
 * seed and the live comparison so their shapes can never drift apart.
 * Limited to url+source: `cloudflareId`/`featured` aren't reliably present on
 * the locally-derived shape (`hostPhotosFromContext`/`sortedPhotos` don't
 * carry `featured`), so including them would make every listing with a
 * cloudflareId-bearing host photo compare as permanently dirty. */
const photoIdentitySnapshot = (list) => JSON.stringify(
    (list ?? []).map((p) => ({ url: photoUrl(p), source: p.source })),
);

const hostPhotosFromContext = (photos) =>
    (photos ?? [])
        .filter(p => p.source === 'host' || !p.source)
        .map(p => ({
            cloudflareId: p.cloudflareId,
            url:          p.url,
            source:       p.source ?? 'host',
            preview:      p.url,
            category:     p.category ?? null,
            quality:      p.quality ?? null,
            extras:       p.extras ?? [],
        }));

const acceptedPlacesFromContext = (photos) =>
    (photos ?? [])
        .filter(p => p.source && p.source !== 'host')
        .map(p => ({
            url:        p.url,
            source:     p.source,
            label:      p.label ?? SOURCE_LABELS[p.source] ?? 'Google Places',
            preview:    p.url,
            category:   p.category ?? null,
            quality:    p.quality ?? null,
            extras:     p.extras ?? [],
        }));

/** API may return strings or { url } objects; always normalize for rendering. */
const normalizeCandidate = (candidate) => {
    if (typeof candidate === 'string') {
        return { url: candidate, source: 'places', label: 'Google Places' };
    }
    return {
        url:        candidate?.url ?? '',
        source:     candidate?.source ?? 'places',
        label:      candidate?.label ?? 'Google Places',
        category:   candidate?.category ?? null,
        confidence: candidate?.confidence ?? null,
        quality:    candidate?.quality ?? null,
        extras:     candidate?.extras ?? [],
    };
};

function photoSourceLabel(photo) {
    return SOURCE_LABELS[photo.source] ?? 'Your photo';
}

function photoMetadataText(photo) {
    const source = photoSourceLabel(photo);
    if (!photo.category) return source;
    return `${source} · ${photo.category}`;
}

function PhotoMetadataLine({ photo, classifying }) {
    const url = photoUrl(photo);
    const isAnalyzing = classifying?.has(url)
        || (photo.preview && photo.preview !== url && classifying?.has(photo.preview));

    if (isAnalyzing) {
        return (
            <p className="mt-1.5 text-[11px] text-gray-500 font-medium truncate">
                <span className="text-[11px] text-gray-400 italic animate-pulse">Analyzing…</span>
            </p>
        );
    }

    return (
        <p className="mt-1.5 text-[11px] text-gray-500 font-medium truncate">
            {photoMetadataText(photo)}
        </p>
    );
}

const IMAGE_ACTION_BTN =
    'flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors';

function PhotoTopRightActions({ isFeatured, onStarClick, onExpandClick, onRemoveClick }) {
    return (
        <div className="absolute top-2 right-2 z-20 flex items-center gap-1">
            <button
                type="button"
                className={IMAGE_ACTION_BTN}
                aria-label={isFeatured ? 'Featured photo' : 'Set as featured photo'}
                onClick={onStarClick}
            >
                {isFeatured ? (
                    <StarIconSolid className="h-4 w-4 text-yellow-400" />
                ) : (
                    <StarIconOutline className="h-4 w-4 text-white/80" />
                )}
            </button>
            <button
                type="button"
                className={IMAGE_ACTION_BTN}
                aria-label="View full size"
                onClick={onExpandClick}
            >
                <ArrowsPointingOutIcon className="h-4 w-4" />
            </button>
            {onRemoveClick && (
                <button
                    type="button"
                    className={IMAGE_ACTION_BTN}
                    aria-label="Remove photo"
                    onClick={onRemoveClick}
                >
                    <XMarkIcon className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}

export default function Step4Photos({ nextStep, previousStep, exitWizard, embedded = false, saveRef = null }) {
    const { photos, setPhotos, listingId, hasAddress, isHydrated } = useWizard();

    usePhotosStepBodyScrollLock();

    const [localPhotos, setLocalPhotos]             = useState(() => hostPhotosFromContext(photos));
    const [acceptedPlaces, setAcceptedPlaces]       = useState(() => acceptedPlacesFromContext(photos));
    const [uploading, setUploading]                 = useState(false);
    const [candidatePhotos, setCandidatePhotos]     = useState([]);
    const [lightbox, setLightbox]                   = useState(null);
    const [featuredUrl, setFeaturedUrl]             = useState(() => {
        const starred = (photos ?? []).find(p => p.featured);
        if (starred) return photoUrl(starred);
        if (photos?.[0]) return photoUrl(photos[0]);
        return null;
    });
    const [classifying, setClassifying]             = useState(() => new Set());

    // Gate that flips true once local state mirrors authoritative context.
    // New-property mode starts hydrated, so this is true at mount and the
    // mirror effect below runs immediately. Resume mode starts false; the
    // sync effect copies server values into local state on the
    // false→true transition and only then unblocks the mirror. Mirrors the
    // pattern in Step5Pricing.jsx (PP-365 / PR #137).
    const [syncedFromHydration, setSyncedFromHydration] = useState(isHydrated);

    // Read `photos` via a ref so the sync effect's deps only list its real
    // trigger (the hydration transition) — keeping `photos` in the deps would
    // re-run the effect on every keystroke after sync.
    const photosRef = useRef(photos);
    useEffect(() => { photosRef.current = photos; }, [photos]);

    const initialPhotosJsonRef = useRef(photoIdentitySnapshot(photos));

    useEffect(() => {
        if (!isHydrated || syncedFromHydration) return;
        const hydratedPhotos = photosRef.current;
        setLocalPhotos(hostPhotosFromContext(hydratedPhotos));
        setAcceptedPlaces(acceptedPlacesFromContext(hydratedPhotos));
        const starred = (hydratedPhotos ?? []).find(p => p.featured);
        setFeaturedUrl(
            starred ? photoUrl(starred) : (hydratedPhotos?.[0] ? photoUrl(hydratedPhotos[0]) : null)
        );
        initialPhotosJsonRef.current = photoIdentitySnapshot(hydratedPhotos);
        setSyncedFromHydration(true);
    }, [isHydrated, syncedFromHydration]);

    useEffect(() => {
        if (!listingId) return;
        axios.get(`/listings/${listingId}/enrichment`)
            .then(res => {
                setCandidatePhotos(res.data?.fields?.candidatePhotos ?? []);
            })
            .catch(() => setCandidatePhotos([]));
    }, [listingId]);

    const selectedPhotos = useMemo(
        () => [
            ...acceptedPlaces.map(p => ({
                ...p,
                url:     p.url,
                preview: p.preview ?? p.url,
            })),
            ...localPhotos.map(p => ({
                ...p,
                url:     p.url || p.preview,
                preview: p.preview || p.url,
            })),
        ],
        [acceptedPlaces, localPhotos]
    );

    useEffect(() => {
        if (selectedPhotos.length > 0 && !featuredUrl) {
            setFeaturedUrl(photoUrl(selectedPhotos[0]));
        }
    }, [selectedPhotos, featuredUrl]);

    useEffect(() => {
        if (selectedPhotos.length === 0) {
            if (featuredUrl) setFeaturedUrl(null);
            return;
        }
        const urls = selectedPhotos.map(photoUrl);
        if (featuredUrl && !urls.includes(featuredUrl)) {
            setFeaturedUrl(urls[0] ?? null);
        }
    }, [selectedPhotos, featuredUrl]);

    const sortedPhotos = useMemo(
        () => [...selectedPhotos].sort((a, b) => {
            const aUrl = photoUrl(a);
            const bUrl = photoUrl(b);
            if (aUrl === featuredUrl) return -1;
            if (bUrl === featuredUrl) return 1;
            return 0;
        }),
        [selectedPhotos, featuredUrl]
    );

    const buildSyncPhotos = useCallback(() => {
        const hostByUrl = new Map(
            localPhotos
                .filter((p) => !p.file && p.url)
                .map((p) => [photoUrl(p), {
                    cloudflareId: p.cloudflareId,
                    url:          p.url,
                    source:       p.source ?? 'host',
                    category:     p.category ?? undefined,
                    quality:      p.quality ?? undefined,
                    extras:       p.extras ?? undefined,
                }]),
        );

        const placesByUrl = new Map(
            acceptedPlaces.map(p => [p.url, {
                url:      p.url,
                source:   p.source ?? 'places',
                label:    p.label ?? SOURCE_LABELS[p.source] ?? 'Google Places',
                category: p.category ?? undefined,
                quality:  p.quality ?? undefined,
                extras:   p.extras ?? undefined,
            }]),
        );

        return sortedPhotos
            .map((photo) => {
                const url = photoUrl(photo);
                const local = localPhotos.find(
                    lp => lp === photo || photoUrl(lp) === url,
                );
                if (local?.file) return null;
                return hostByUrl.get(url) ?? placesByUrl.get(url) ?? null;
            })
            .filter(Boolean)
            .map((p, index) => ({
                ...p,
                featured: index === 0,
            }));
    }, [sortedPhotos, localPhotos, acceptedPlaces]);

    useEffect(() => {
        if (!syncedFromHydration) return;
        const synced = buildSyncPhotos();
        setPhotos(synced);
    }, [buildSyncPhotos, setPhotos, syncedFromHydration]);

    const { flush } = useStepAutosave({
        listingId,
        hasAddress: hasAddress && isHydrated,
        value: photos,
        buildPayload: (v) => ({ photos: v }),
    });

    const candidateUrlSet = useMemo(
        () => new Set(
            candidatePhotos
                .map(normalizeCandidate)
                .map(c => c.url)
                .filter(Boolean)
        ),
        [candidatePhotos]
    );

    const isSuggestedUrl = useCallback(
        (url) => candidateUrlSet.has(url),
        [candidateUrlSet]
    );

    const visibleCandidates = useMemo(
        () => candidatePhotos
            .map(normalizeCandidate)
            .filter(c => c.url && !acceptedPlaces.some(p => p.url === c.url)),
        [candidatePhotos, acceptedPlaces]
    );

    const uploadImagesToCloudflare = async (images, uploadUrls) => {
        const MAX_RETRIES = 3;

        const uploadImage = async (image, url, retries = 0) => {
            const formData = new FormData();
            formData.append('file', image.file);
            try {
                return await externalAxios.post(url, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 30000,
                });
            } catch {
                if (retries < MAX_RETRIES) {
                    return uploadImage(image, url, retries + 1);
                }
                return null;
            }
        };

        const results = await Promise.all(
            images.map((image, index) => uploadImage(image, uploadUrls[index]))
        );
        return results.filter(result => result !== null);
    };

    const acceptCandidate = (candidate) => {
        setAcceptedPlaces(prev => {
            if (prev.some(p => p.url === candidate.url)) return prev;
            return [...prev, {
                url:        candidate.url,
                source:     candidate.source ?? 'places',
                label:      candidate.label ?? SOURCE_LABELS[candidate.source] ?? 'Google Places',
                preview:    candidate.url,
                category:   candidate.category ?? null,
                quality:    candidate.quality ?? null,
                extras:     candidate.extras ?? [],
            }];
        });
    };

    const classifyManualPhoto = useCallback(async (url) => {
        if (!listingId) return;
        setClassifying(prev => new Set(prev).add(url));
        try {
            const res = await axios.post(`/listings/${listingId}/classify-photo`, { url });
            setLocalPhotos(prev => prev.map(p =>
                (p.url === url || p.preview === url)
                    ? {
                        ...p,
                        category: res.data.category,
                        quality:  res.data.quality,
                        extras:   res.data.extras ?? [],
                    }
                    : p
            ));
        } catch {
            // Best-effort — silently skip, photo stays without classification
        } finally {
            setClassifying(prev => {
                const next = new Set(prev);
                next.delete(url);
                return next;
            });
        }
    }, [listingId]);

    const uploadHostPhotoToCloudflare = useCallback(async (file, preview) => {
        if (!listingId) return;
        setClassifying(prev => new Set(prev).add(preview));
        try {
            const urlRes = await axios.get('/property/image-upload-url/1');
            assertUploadUrls(urlRes.data, 1);
            const results = await uploadImagesToCloudflare([{ file, preview }], urlRes.data);
            if (results.length === 0) return;

            const deliveryUrl = results[0].data.result.variants[0];
            const cloudflareId = results[0].data.result.id;

            setLocalPhotos(prev => prev.map(p =>
                p.preview === preview
                    ? { ...p, url: deliveryUrl, preview: deliveryUrl, cloudflareId, file: undefined }
                    : p
            ));

            setClassifying(prev => {
                const next = new Set(prev);
                next.delete(preview);
                next.add(deliveryUrl);
                return next;
            });
            classifyManualPhoto(deliveryUrl);
        } catch {
            setClassifying(prev => {
                const next = new Set(prev);
                next.delete(preview);
                return next;
            });
        }
    }, [listingId, classifyManualPhoto]);

    const handleStarClick = (e, item, { isCandidate = false } = {}) => {
        e.stopPropagation();
        const url = isCandidate ? item.url : photoUrl(item);
        if (isCandidate) {
            if (!acceptedPlaces.some(p => p.url === url)) {
                acceptCandidate(item);
            }
            setFeaturedUrl(url);
        } else {
            setFeaturedUrl(url);
        }
    };

    const openLightbox = useCallback((photos, index) => {
        setLightbox({ photos, index });
    }, []);

    const navigateLightbox = useCallback((step) => {
        setLightbox(lb => {
            if (!lb || lb.photos.length === 0) return lb;
            let next = lb.index + step;
            if (next < 0) next = lb.photos.length - 1;
            if (next >= lb.photos.length) next = 0;
            return { ...lb, index: next };
        });
    }, []);

    const handleFileChange = (acceptedFiles) => {
        const validFileTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const filteredFiles = acceptedFiles.filter(
            file => file.size <= MAX_FILE_SIZE && validFileTypes.includes(file.type)
        );
        if (filteredFiles.length < acceptedFiles.length) {
            toastWarn(
                `${acceptedFiles.length - filteredFiles.length} file(s) was not uploaded. Maximum file size is 5 MB. Accepted file types are .jpeg, .png, and .webp.`
            );
        }
        const filesArray = filteredFiles.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            source:  'host',
            extras:  [],
        }));
        if (filesArray.length > 0) {
            setLocalPhotos(prev => [...prev, ...filesArray]);
            filesArray.forEach(({ file, preview }) => {
                uploadHostPhotoToCloudflare(file, preview);
            });
        }
    };

    const handleDropRejected = () => {
        toastWarn('File not accepted. Maximum file size is 5 MB. Accepted file types are JPEG, PNG, and WebP.');
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        onDrop: handleFileChange,
        onDropRejected: handleDropRejected,
        accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] },
        maxSize: MAX_FILE_SIZE,
    });

    const handleRemoveSelected = (e, photo) => {
        e.stopPropagation();
        const url = photoUrl(photo);
        if (isSuggestedUrl(url)) {
            setAcceptedPlaces(prev => prev.filter(p => p.url !== url));
        } else {
            setLocalPhotos(prev =>
                prev.filter(img => img.preview !== photo.preview && img.url !== photo.url)
            );
            if (photo.preview && photo.file) {
                URL.revokeObjectURL(photo.preview);
            }
        }
    };

    const buildMergedPhotos = async () => {
        if (!listingId) return null;

        const toUpload = localPhotos
            .map((p, index) => ({ p, index }))
            .filter(({ p }) => p.file);

        const uploadResultsByIndex = {};

        if (toUpload.length > 0) {
            const urlRes = await axios.get(`/property/image-upload-url/${toUpload.length}`);
            const uploadUrls = urlRes.data;
            assertUploadUrls(uploadUrls, toUpload.length);

            const filesToUpload = toUpload.map(({ p }) => p);
            const results = await uploadImagesToCloudflare(filesToUpload, uploadUrls);

            if (results.length !== toUpload.length) {
                throw new Error('Failed to upload all photos');
            }

            toUpload.forEach(({ index }, j) => {
                uploadResultsByIndex[index] = {
                    cloudflareId: results[j].data.result.id,
                    url:          results[j].data.result.variants[0],
                    source:       'host',
                };
            });
        }

        const hostPhotos = localPhotos.map((p, index) => {
            if (p.file) {
                const uploaded = uploadResultsByIndex[index];
                return {
                    ...uploaded,
                    category: p.category ?? undefined,
                    quality:  p.quality ?? undefined,
                    extras:   p.extras ?? undefined,
                };
            }
            return {
                cloudflareId: p.cloudflareId,
                url:          p.url,
                source:       p.source ?? 'host',
                category:     p.category ?? undefined,
                quality:      p.quality ?? undefined,
                extras:       p.extras ?? undefined,
            };
        });

        const placesPhotos = acceptedPlaces.map(p => ({
            url:      p.url,
            source:   p.source ?? 'places',
            label:    p.label ?? SOURCE_LABELS[p.source] ?? 'Google Places',
            category: p.category ?? undefined,
            quality:  p.quality ?? undefined,
            extras:   p.extras ?? undefined,
        }));

        const toSavedPhoto = (photo) => {
            const hostIndex = localPhotos.findIndex(
                lp => lp === photo || photoUrl(lp) === photoUrl(photo)
            );
            if (hostIndex >= 0) {
                return hostPhotos[hostIndex];
            }
            return placesPhotos.find(pp => pp.url === photo.url) ?? null;
        };

        return sortedPhotos
            .map(toSavedPhoto)
            .filter(Boolean)
            .map((p, index) => ({
                ...p,
                featured: index === 0,
            }));
    };

    useImperativeHandle(saveRef, () => ({
        isDirty: () => photoIdentitySnapshot(sortedPhotos) !== initialPhotosJsonRef.current,
        save: async () => {
            setUploading(true);
            try {
                const merged = await buildMergedPhotos();
                if (!merged) return null;
                setPhotos(merged);
                return merged;
            } finally {
                setUploading(false);
            }
        },
    }), [acceptedPlaces, listingId, localPhotos, setPhotos, sortedPhotos]);

    const saveDraft = async () => {
        const merged = await buildMergedPhotos();
        if (!merged) return;
        setPhotos(merged);
        await flush(merged);
    };

    const handleNext = async () => {
        // `uploading` doubles as the re-entrancy guard: the button is no longer
        // disabled by the background autosave, so a second click can land while
        // the first Next is still merging/uploading.
        if (!listingId || uploading) return;

        setUploading(true);
        try {
            await saveDraft();
            nextStep();
        } catch (err) {
            if (isMissingAddressAutosaveError(err)) return;
            if (err instanceof UploadUrlsError) {
                toastError('Could not get upload URLs — check Cloudflare configuration.');
            } else {
                toastError('Photo upload failed. Please try again.');
            }
        } finally {
            setUploading(false);
        }
    };

    const handleSaveAndExit = async () => {
        if (!listingId) {
            exitWizard?.();
            return;
        }
        setUploading(true);
        try {
            await saveDraft();
            exitWizard?.();
        } catch (err) {
            if (isMissingAddressAutosaveError(err)) return;
            if (err instanceof UploadUrlsError) {
                toastError('Could not get upload URLs — check Cloudflare configuration.');
            } else {
                toastError('Failed to save. Please try again.');
            }
        } finally {
            setUploading(false);
        }
    };

    // Foreground only. `uploading` is click-initiated (Next / Save-and-exit /
    // the imperative save), and it already spans the flush those handlers await.
    // The background autosave must NOT appear here: a `disabled` Next button
    // silently swallows the click that follows a photo edit + pause, which is
    // the footer half of PP-627.
    const busy = uploading;

    const hostPhotoLabel = localPhotos.length === 1 ? 'host photo' : 'host photos';
    const suggestedTotal = candidatePhotos.length;
    const canAdvance = selectedPhotos.length > 0;

    const coverage = useMemo(
        () => COVERAGE_CATEGORIES.map(cat => {
            const count = selectedPhotos.filter(p =>
                p.category === cat || (p.extras ?? []).includes(cat)
            ).length;
            return { label: cat, count, covered: count > 0 };
        }),
        [selectedPhotos]
    );

    const stepFooter = (
        <WizardStepFooter
            back={(
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={previousStep}
                    disabled={busy}
                >
                    ← Back
                </button>
            )}
            tertiary={(
                <SaveContinueLaterButton
                    onSaveAndExit={handleSaveAndExit}
                    disabled={!listingId}
                    busy={busy}
                />
            )}
            primary={(
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleNext}
                    disabled={!canAdvance || busy}
                >
                    {uploading ? 'Uploading…' : 'Next: Pricing'}
                </button>
            )}
        />
    );

    const innerContent = (
            <div className="flex flex-col gap-4 pb-4">

                {candidatePhotos.length > 0 && (
                    <div>
                        <div className="flex gap-3 items-start bg-blue-50 border border-[rgba(26,39,68,0.18)] rounded-xl p-3.5 mb-4">
                            <div
                                className="shrink-0 flex items-center justify-center rounded-lg p-2 text-white"
                                style={{ backgroundColor: '#1A2744', width: 36, height: 36, minWidth: 36, minHeight: 36 }}
                                aria-hidden="true"
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5z" />
                                    <circle cx="19" cy="5" r="1.5" />
                                    <circle cx="5" cy="19" r="1.5" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[12px] font-bold text-gray-900 mb-0.5">
                                    We found {candidatePhotos.length} suggested photo{candidatePhotos.length !== 1 ? 's' : ''} for this property
                                </p>
                                <p className="text-[11px] text-gray-500 leading-[1.5]">
                                    Please review the suggested photos. Choose the ones that best represent your property, then upload additional photos if needed.
                                </p>
                            </div>
                        </div>
                        <p className={PHOTO_SECTION_TITLE}>
                            Suggested photos
                        </p>
                        {visibleCandidates.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {visibleCandidates.map((candidate, index) => {
                                const isFeatured = featuredUrl === candidate.url;
                                return (
                                    <div key={candidate.url || `candidate-${index}`} className="relative">
                                        <div className="relative w-full overflow-hidden rounded-lg bg-gray-100 shadow-sm">
                                            <div className="w-full pb-[56.25%]" aria-hidden="true" />
                                            <img
                                                src={candidate.url}
                                                alt={candidate.label ?? 'Google Places'}
                                                referrerPolicy="no-referrer"
                                                className="absolute inset-0 h-full w-full object-cover"
                                            />
                                            {candidate.quality && PHOTO_QUALITY_TO_CONFIDENCE[candidate.quality] && (
                                                <span className="absolute top-2 left-2 z-10">
                                                    <ConfidenceBadge
                                                        confidence={PHOTO_QUALITY_TO_CONFIDENCE[candidate.quality]}
                                                        labelVariant="info"
                                                        solid
                                                    />
                                                </span>
                                            )}
                                            <PhotoTopRightActions
                                                isFeatured={isFeatured}
                                                onStarClick={(e) => handleStarClick(e, candidate, { isCandidate: true })}
                                                onExpandClick={(e) => {
                                                    e.stopPropagation();
                                                    openLightbox(visibleCandidates, index);
                                                }}
                                            />
                                            <button
                                                type="button"
                                                className="absolute bottom-2 right-2 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-all duration-200 bg-black/50 text-white hover:bg-black/60"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    acceptCandidate(candidate);
                                                }}
                                            >
                                                <span
                                                    className="h-4 w-4 shrink-0 rounded border-2 border-white"
                                                    aria-hidden
                                                />
                                                Use This Photo
                                            </button>
                                        </div>
                                        <PhotoMetadataLine photo={candidate} classifying={classifying} />
                                    </div>
                                );
                            })}
                        </div>
                        )}
                        <p className="mt-2 text-xs text-gray-500 text-center">
                            {acceptedPlaces.length} selected · {visibleCandidates.length} remaining in suggestions
                        </p>
                    </div>
                )}

                {lightbox && lightbox.photos.length > 0 && (
                    <PhotoLightbox
                        photos={lightbox.photos}
                        index={lightbox.index}
                        onClose={() => setLightbox(null)}
                        onNavigate={navigateLightbox}
                    />
                )}

                {candidatePhotos.length === 0 && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex gap-3 text-sm text-gray-600">
                        <InformationCircleIcon className="w-5 h-5 shrink-0 text-gray-400" aria-hidden="true" />
                        <p>
                            <span className="font-medium text-gray-700">We couldn't find any candidate photos for this property.</span>
                            {' '}Please upload photos that help drivers recognize your location when they arrive. At least one photo is required.
                        </p>
                    </div>
                )}

                {selectedPhotos.length > 0 && (
                    <div>
                        <p className={classNames(PHOTO_SECTION_TITLE, 'mt-4')}>
                            Your selected photos
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {sortedPhotos.map((photo, index) => {
                                const url = photoUrl(photo);
                                const isFeatured = featuredUrl === url;
                                const quality = photo.quality;
                                return (
                                    <div key={url || `selected-${index}`} className="relative">
                                        <div className="relative w-full overflow-hidden rounded-lg bg-gray-100 shadow-sm">
                                            <div className="w-full pb-[56.25%]" aria-hidden="true" />
                                            <img
                                                src={photo.preview || photo.url}
                                                alt="Selected photo"
                                                referrerPolicy="no-referrer"
                                                className="absolute inset-0 h-full w-full object-cover"
                                            />
                                            {quality && PHOTO_QUALITY_TO_CONFIDENCE[quality] && (
                                                <span className="absolute top-2 left-2 z-10">
                                                    <ConfidenceBadge
                                                        confidence={PHOTO_QUALITY_TO_CONFIDENCE[quality]}
                                                        labelVariant="info"
                                                        solid
                                                    />
                                                </span>
                                            )}
                                            <PhotoTopRightActions
                                                isFeatured={isFeatured}
                                                onStarClick={(e) => handleStarClick(e, photo)}
                                                onExpandClick={(e) => {
                                                    e.stopPropagation();
                                                    openLightbox(
                                                        sortedPhotos.map(p => ({ ...p, url: photoUrl(p) })),
                                                        index
                                                    );
                                                }}
                                                onRemoveClick={(e) => handleRemoveSelected(e, photo)}
                                            />
                                        </div>
                                        <PhotoMetadataLine photo={photo} classifying={classifying} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div
                    className="bg-gray-50 border border-gray-200 rounded-lg px-4 mb-3"
                    style={{ paddingTop: 12, paddingBottom: 12 }}
                >
                    <p className="text-[11px] font-bold tracking-[0.4px] uppercase text-gray-400 mb-1">
                        Suggested Photo Types
                    </p>
                    <p className="text-[12px] text-gray-500 mb-2">
                        These photo types can help drivers identify your property.
                        Only one photo is required, but adding more can make your
                        property easier to recognize.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {coverage.map(({ label, count, covered }) => (
                            <span
                                key={label}
                                className={classNames(
                                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border leading-tight',
                                    covered
                                        ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'
                                        : 'bg-gray-50 text-gray-400 border-gray-200'
                                )}
                            >
                                <span className="text-[10px] leading-none shrink-0">{covered ? '✓' : '⬚'}</span>
                                {label}{covered ? ` · ${count}` : ''}
                            </span>
                        ))}
                    </div>
                </div>

                <section
                    className={classNames(
                        'border-2 border-dashed border-gray-300 rounded-lg px-3',
                        selectedPhotos.length > 0 ? 'py-4' : 'py-6'
                    )}
                >
                    <div {...getRootProps()} className="flex flex-col justify-center items-center space-y-4 cursor-pointer">
                        <PhotoIcon
                            className={classNames(
                                'text-gray-500 mx-auto',
                                selectedPhotos.length > 0 ? 'h-12 w-12' : 'h-20 w-20'
                            )}
                        />
                        <input {...getInputProps()} />
                        <p className="text-center text-sm text-gray-500">
                            Drag and drop
                            <br />
                            <span>or </span>
                            <span
                                className="text-blue-600 hover:underline"
                                role="button"
                                tabIndex={0}
                                onClick={e => { e.stopPropagation(); open(); }}
                                onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); open(); } }}
                            >
                                Browse
                            </span>
                        </p>
                    </div>
                </section>

                <p className="text-xs text-gray-500 text-center">
                    {acceptedPlaces.length}/{suggestedTotal} Suggested Photos Accepted · {localPhotos.length} {hostPhotoLabel} · Max 5 MB / file · JPEG, PNG, WebP
                </p>
            </div>
    );

    if (embedded) return innerContent;

    return (
        <WizardStepLayout
            title={WIZARD_PAGE_COPY.photos.title}
            description={WIZARD_PAGE_COPY.photos.description}
            footer={stepFooter}
        >
            {innerContent}
        </WizardStepLayout>
    );
}
